var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { Component } from 'react';
import { Subject, BehaviorSubject, merge } from 'rxjs';
import { pairwise, startWith, distinctUntilChanged, takeUntil, filter, switchMap, delay, debounceTime, withLatestFrom, throttleTime } from 'rxjs/operators';
import { equals, prop, pipe, eqBy, isEmpty, isNil, always, anyPass, when, path } from 'ramda';
import { isElementBlock, isNotNil } from '../../services/utils/_.utils';
import { FormContext } from '../../services/form/form-context';
;
var mapElementValue = when(anyPass([isNil, isEmpty]), always(null));
var FormBaseElement = /** @class */ (function (_super) {
    __extends(FormBaseElement, _super);
    function FormBaseElement(props) {
        var _this = _super.call(this, props) || this;
        _this.destroy$ = new Subject();
        _this.changeStamp$ = new BehaviorSubject(0);
        _this.initialized = false;
        _this.rendered = false;
        var def = props.initialDef || null;
        var value = def && mapElementValue(def.value);
        var activeIndex = def.frontOptions.activeIndex;
        activeIndex = typeof activeIndex === 'string' ? JSON.parse(activeIndex || null) : activeIndex;
        _this.state = { value: value, def: def, prevDef: def, prevValue: value, error: null, touched: false, focused: false, activeIndex: activeIndex };
        _this.handleChange = _this.handleChange.bind(_this);
        _this.handleBlur = _this.handleBlur.bind(_this);
        _this.handleFocus = _this.handleFocus.bind(_this);
        return _this;
    }
    Object.defineProperty(FormBaseElement.prototype, "changeStamp", {
        get: function () {
            return this.changeStamp$.getValue();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "renderService", {
        get: function () { return this.props.renderService; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "form", {
        get: function () { return this.renderService.context; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "def", {
        get: function () { return this.state.def; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "formulaDef", {
        get: function () {
            var def = this.renderService.findFormulaDefinition(this.def.id);
            if (def) {
                def.value = this.value;
            }
            return def;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "focused", {
        get: function () {
            return this.state.focused;
        },
        set: function (focused) {
            var _this = this;
            this.setState(function () {
                _this.rendered = false;
                return { focused: focused };
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "error", {
        get: function () { return this.state.error; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "touched", {
        get: function () { return this.state.touched; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "value", {
        get: function () {
            return this.state.value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "freezeChanges", {
        get: function () {
            return this.focused && !this.isInputEnded;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormBaseElement.prototype, "isInputEnded", {
        get: function () {
            return Date.now() - this.changeStamp > FormContext.CHANGE_DEBOUNCE_TIME + 10;
        },
        enumerable: true,
        configurable: true
    });
    FormBaseElement.prototype.componentDidMount = function () {
        var _this = this;
        // element def changes
        var changes$ = this.props.def$.pipe(distinctUntilChanged(this.shouldDefinitionSkip.bind(this)));
        var lastChanges$ = this.changeStamp$.pipe(debounceTime(FormContext.CHANGE_DEBOUNCE_TIME), switchMap(function () {
            return _this.form.afterChanges$().pipe(delay(FormContext.CHANGE_DEBOUNCE_TIME), withLatestFrom(changes$, function (_, def) { return def; }));
        }));
        var element$ = merge(changes$, lastChanges$).pipe(startWith(this.def), throttleTime(FormContext.CHANGE_DEBOUNCE_TIME), filter(function (next) {
            if (_this.focused && _this.isInputEnded && next.lastChanged > _this.changeStamp) {
                _this.setValue(next.value, true);
            }
            return !_this.freezeChanges;
        }), pairwise(), takeUntil(this.destroy$));
        // element validation
        var validation$ = this.props.error$.pipe(distinctUntilChanged(equals), takeUntil(this.destroy$));
        // subscribe on element changes
        element$.subscribe(function (_a) {
            var prevDef = _a[0], def = _a[1];
            _this.setState(function () {
                var value = mapElementValue(def.lastChanged <= _this.changeStamp ? _this.value : prop('value', def));
                var activeIndex = def.frontOptions.activeIndex;
                activeIndex = typeof activeIndex === 'string' ? JSON.parse(activeIndex || null) : activeIndex;
                var prevValue = mapElementValue(_this.value);
                _this.rendered = false;
                return { def: def, prevDef: prevDef, value: value, prevValue: prevValue, activeIndex: activeIndex };
            });
            // }
        });
        // subscribe on element validation (error/touched)
        validation$.subscribe(function (validation) {
            var touched = validation.touched, error = validation.error;
            if (touched && !equals(_this.error, error)) {
                _this.rendered = false;
                _this.setState(validation);
            }
        });
    };
    FormBaseElement.prototype.componentWillUnmount = function () {
        this.destroy$.next();
        this.renderService.onElementDestroy(this.def);
    };
    FormBaseElement.prototype.shouldDefinitionSkip = function (prev, next) {
        return FormBaseElement.isDefinitionForSkip(prev, next);
    };
    FormBaseElement.prototype.shouldElementRender = function (state) {
        return !this.initialized || !this.rendered;
    };
    FormBaseElement.prototype.shouldComponentUpdate = function (_, nextState) {
        return nextState.def ? this.shouldElementRender(nextState) : true;
    };
    // use in elementRender for group elements (like panel/tab/table/etc)
    FormBaseElement.prototype.renderChild = function (it, mapDef) {
        var map_ = [mapDef, this.props.mapDef].filter(isNotNil);
        return this.renderService.renderChild(it, this.props.onEvent, map_.length ? pipe.apply(void 0, map_) : null);
    };
    FormBaseElement.prototype.renderChildren = function () {
        var _this = this;
        return isElementBlock(this.def) && this.def.elements
            ? this.def.elements.map(function (c) { return _this.renderChild(c); })
            : (null);
    };
    FormBaseElement.prototype.render = function () {
        if (this.def) {
            this.rendered = this.initialized = true;
            return this.elementRender(this.def);
        }
        return (null);
    };
    // handle blur element
    FormBaseElement.prototype.handleBlur = function (evt) {
        this.focused = false;
        this.form.handleBlur(this.def.id, evt);
        this.form.handleChange(this.def.id, this.value, true);
    };
    // handle focus for text fields
    FormBaseElement.prototype.handleFocus = function (evt) {
        this.focused = true;
    };
    // handle value changes
    FormBaseElement.prototype.handleChange = function (value, immediatly) {
        if (immediatly === void 0) { immediatly = false; }
        this.changeStamp$.next(Date.now());
        this.setValue(value);
        this.form.handleChange(this.def.id, value, immediatly);
        this.form.handleTouched(this.def.id, true, immediatly);
    };
    FormBaseElement.prototype.handleActiveIndexChange = function (activeIndex) {
        this.setState({ activeIndex: activeIndex }, this.forceUpdate);
        this.form.handleActiveIndexChange(this.def.id, activeIndex);
    };
    // set element value
    FormBaseElement.prototype.setValue = function (value, force) {
        var _this = this;
        if (force === void 0) { force = false; }
        this.rendered = false;
        this.setState({ value: value }, function () { return force && _this.forceUpdate(); });
    };
    FormBaseElement.isDefinitionForSkip = eqBy(path(['lastChanged']));
    return FormBaseElement;
}(Component));
export { FormBaseElement };
;
//# sourceMappingURL=FormBaseElement.js.map